import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Award, BookOpen, Download, Sparkles, ArrowRight, PlayCircle,
  Headphones, FileText, Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/* ---------- Visual ring that shows the student's overall progress ---------- */
function ProgressRing({ pct = 0, size = 140, stroke = 10 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (c * pct) / 100;
  return (
    <div className="relative inline-block flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="block">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#FDE7EF" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#E91E63" strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display text-neutral-900" style={{ fontSize: size * 0.26, fontWeight: 700, lineHeight: 1 }}>
          {pct}<span className="text-brand-pink" style={{ fontSize: size * 0.18 }}>%</span>
        </span>
      </div>
    </div>
  );
}

function StatPill({ Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white p-5 border border-neutral-100 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-brand-blush flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-brand-pink" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-2xl leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

function CourseProgressCard({ course }) {
  const pct = course.progress_pct || 0;
  const done = course.lessons_done || 0;
  const total = course.lessons_total || 0;
  const nextUp = course.next_up;
  const isDone = total > 0 && done === total;

  return (
    <div
      data-testid={`dashboard-course-${course.id}`}
      className="rounded-3xl bg-white border border-neutral-100 overflow-hidden hover:-translate-y-0.5 transition-transform shadow-sm"
    >
      <div className="flex gap-5 p-5">
        <div className="w-28 flex-shrink-0 rounded-2xl overflow-hidden">
          <img src={course.cover_image} alt="" className="w-full h-full object-cover aspect-square" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-brand-pink mb-1">{course.category}</div>
          <div className="font-display text-lg leading-tight line-clamp-2 mb-2">{course.title}</div>
          <div className="flex items-center gap-3 text-xs text-neutral-500 mb-3">
            <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> {done}/{total}</span>
            <span className="inline-flex items-center gap-1"><Headphones className="w-3 h-3" /> narrated</span>
          </div>
          {/* Progress bar */}
          <div className="relative h-2 rounded-full bg-brand-blush overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-brand-pink rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-neutral-500">{pct}% complete</span>
            {isDone && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-brand-pink">
                <Trophy className="w-3 h-3" fill="#E91E63" /> Finished
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Next-up CTA */}
      {nextUp && !isDone ? (
        <Link
          to={`/courses/${course.id}`}
          data-testid={`dashboard-next-up-${course.id}`}
          className="block border-t border-neutral-100 bg-brand-blush/40 px-5 py-3 hover:bg-brand-blush transition group"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-brand-pink">Next up</div>
              <div className="text-sm font-medium text-neutral-900 truncate">{nextUp.lesson_title}</div>
              <div className="text-xs text-neutral-500 truncate">
                {nextUp.module_title}
                {nextUp.read_minutes ? ` · ${nextUp.read_minutes} min read` : ""}
              </div>
            </div>
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-pink text-white group-hover:scale-105 transition flex-shrink-0 ml-3">
              <PlayCircle className="w-5 h-5" />
            </span>
          </div>
        </Link>
      ) : isDone ? (
        <Link
          to={`/courses/${course.id}`}
          className="block border-t border-neutral-100 bg-brand-pink/10 px-5 py-3 hover:bg-brand-pink/15 transition text-center"
        >
          <span className="text-xs uppercase tracking-widest text-brand-pink font-medium inline-flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> Review the course
          </span>
        </Link>
      ) : (
        <Link
          to={`/courses/${course.id}`}
          className="block border-t border-neutral-100 px-5 py-3 text-center text-xs uppercase tracking-widest text-brand-pink hover:bg-brand-blush/40 transition"
        >
          Open course
        </Link>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setData(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const purchased = data?.purchased_products || [];
  const purchasedProducts = products.filter((p) => purchased.includes(p.id));
  const stats = data?.stats || { overall_pct: 0, total_done: 0, total_lessons: 0, courses_count: 0, courses_completed: 0 };

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <Navbar />
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Your Dashboard</div>
            <h1 className="font-display text-4xl md:text-5xl">
              Hi {user?.name?.split(" ")[0]} 👋 <span className="font-script text-brand-pink text-5xl">welcome back</span>
            </h1>
          </div>
          <Button asChild className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white" data-testid="dashboard-ai-btn">
            <Link to="/ai-tools"><Sparkles className="w-4 h-4 mr-2" /> Open AI Tools</Link>
          </Button>
        </div>

        {/* Big progress hero */}
        {stats.courses_count > 0 ? (
          <div
            data-testid="dashboard-progress-hero"
            className="mt-10 rounded-3xl bg-white border border-neutral-100 p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 shadow-sm"
          >
            <ProgressRing pct={stats.overall_pct} />
            <div className="flex-1 min-w-0 text-center md:text-left">
              <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-2">Your overall progress</div>
              <div className="font-display text-3xl mb-3">
                {stats.total_done} of {stats.total_lessons} lessons
                <span className="text-neutral-400"> · </span>
                {stats.courses_completed} course{stats.courses_completed === 1 ? "" : "s"} complete
              </div>
              <p className="text-neutral-600 leading-relaxed max-w-xl">
                {stats.overall_pct === 0
                  ? "Pick your first lesson below, do the action step, and come back tomorrow. Showing up every day is the whole system."
                  : stats.overall_pct < 25
                  ? "Nice start. Momentum is a muscle — one lesson a day beats a weekend binge every time."
                  : stats.overall_pct < 75
                  ? "You're well on your way. Keep the daily lesson habit — every action step compounds."
                  : "You're almost there. Finish strong and claim your certificate."}
              </p>
            </div>
          </div>
        ) : null}

        {/* Stat pills */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <StatPill Icon={BookOpen} label="Courses Enrolled" value={stats.courses_count} />
          <StatPill Icon={FileText} label="Lessons Done" value={stats.total_done} />
          <StatPill Icon={Award} label="Certificates" value={data?.certificates?.length || 0} />
          <StatPill Icon={Download} label="Products Owned" value={purchased.length} />
        </div>

        {/* Continue learning + library */}
        <div className="grid lg:grid-cols-3 gap-6 mt-12">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">Continue Learning</h2>
              <Link to="/courses" className="text-sm text-brand-pink inline-flex items-center gap-1" data-testid="browse-courses-link">
                Browse all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid gap-5">
              {data?.enrolled_courses?.length ? (
                data.enrolled_courses.map((c) => <CourseProgressCard key={c.id} course={c} />)
              ) : (
                <div className="rounded-3xl bg-white p-10 text-center border border-dashed border-neutral-200">
                  <div className="text-neutral-500 mb-4">You're not enrolled yet.</div>
                  <Button asChild className="rounded-full bg-brand-pink text-white">
                    <Link to="/courses">Explore courses</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-6">Your Library</h2>
            <div className="rounded-3xl bg-white border border-neutral-100 p-6 space-y-4">
              {purchasedProducts.length ? (
                purchasedProducts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-neutral-100 last:border-0 pb-3 last:pb-0">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-xs text-neutral-500">{p.tier === "free" ? "Free" : `$${p.price}`}</div>
                    </div>
                    <a href={p.download_url} className="text-brand-pink text-sm inline-flex items-center gap-1 flex-shrink-0">
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                ))
              ) : (
                <div className="text-sm text-neutral-500 text-center py-6">
                  Grab a free download to get started.<br />
                  <Link to="/products" className="text-brand-pink font-medium">Visit the storefront</Link>
                </div>
              )}
            </div>

            {data?.certificates?.length ? (
              <>
                <h2 className="font-display text-2xl mb-6 mt-10">Certificates</h2>
                <div className="rounded-3xl bg-white border border-neutral-100 p-6 space-y-3">
                  {data.certificates.map((c) => (
                    <Link
                      key={c.id}
                      to={`/certificates/${c.course_id}`}
                      data-testid={`cert-link-${c.course_id}`}
                      className="flex items-center justify-between hover:text-brand-pink"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{c.course_title}</div>
                        <div className="text-xs text-neutral-500">
                          Issued {new Date(c.issued_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Award className="w-5 h-5 text-brand-pink flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
